import { X } from 'lucide-react';
import { useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { normalizeazaClase, textDupaStergere } from '../../../utils/clase-canonice';

/**
 * Campul „care clase" de pe activitatea practica (SP-05a), cu oglinda de sub el.
 *
 * 🔴 OGLINDA E PREVIZUALIZARE, NU VALIDARE. Arata ce am inteles, in forma canonica, ca omul sa
 * prinda singur greseala („am scris VI, dar voiam VII") inainte sa trimita. Nu respinge nimic:
 * campul se trimite asa cum l-a scris el, iar serverul are ultimul cuvant. Vezi
 * `utils/clase-canonice.ts` — acolo e si motivul (serverul e sursa de adevar pentru clase;
 * validand local, la prima divergenta un profesor ar lua eroare de la NOI).
 *
 * 🔴 NU RESPINGE CE POATE INTELEGE (directia S1). Fiecare respingere evitabila e un profesor care
 * rescrie ce stie de douazeci de ani. De aceea nu exista aici nici „forma corecta e X", nici camp
 * blocat, nici golire la eroare.
 *
 * 🔴 NUMARUL E DERIVAT, si sta LANGA etichete, nu in camp propriu. Nu se cere niciodata separat:
 * punctajul e `50 x clase`, iar un numar scris de om ar fi o a doua sursa de adevar pentru acelasi
 * fapt. (Pe server e la fel: `numarClase` nu exista in DTO, se deriva din `clase` — verificat in
 * tipurile generate, nu presupus.)
 *
 * ⚠️ CE NU SPUNE ECRANUL ASTA. `POST /api/answers` intoarce 201 chiar si pentru o clasa deja
 * punctata — masurat de V1 pe DEV; 409-ul cu lista claselor duplicate vine abia la APROBARE, si il
 * vede adminul. Deci aici nu se promite „clasele au fost inregistrate/punctate", ci „trimise spre
 * aprobare", si NU se construieste nicio marcare de duplicat pe raspunsul de submisie: acolo nu
 * exista. Verificarea la trimitere ca simpla atentionare (directia S1 §1) e decizie deschisa a
 * owner-ului — pana atunci, nimic.
 */

export interface CampClaseProps {
    /** Textul brut, exact cum l-a scris omul. Asta se trimite la server, nu canonicele. */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    /**
     * Fragmentele pe care SERVERUL nu le-a inteles (`fragmenteNerecunoscute` din 400).
     * Vin de la server, nu din normalizatorul local: cine respinge e tot cine decide.
     */
    fragmenteRespinse?: string[];
    /**
     * B3: eticheta, cand campul sta singur pe ecran (fara titlul-intrebare din `EcranClase`).
     * Implicit „Clasele participante" — playerul de lectie nu se schimba.
     */
    label?: string;
    /** B3: id-ul unei erori a consumatorului (ex. „clase obligatorii"), legat de camp pe langa textul de ajutor. */
    errorId?: string;
}

export function CampClase({ value, onChange, disabled, fragmenteRespinse, label = 'Clasele participante', errorId }: CampClaseProps) {
    const { clase, neintelese } = useMemo(() => normalizeazaClase(value), [value]);

    /**
     * Sterge o eticheta rescriind textul din canonicele ramase — PLUS ce n-am inteles.
     *
     * 🔴 `neintelese` NU se pierde la rescriere. Prima versiune lua doar `clase`, si atunci
     * «a V-a A, gradinita» devenea sir GOL la apasarea lui `×`: „gradinita" disparea fara ca
     * nimeni s-o ceara. Adica exact ce interzice directia („nu sterge ce a scris omul"), doar ca
     * pe calea butonului, nu a erorii — si in cel mai prost moment, pentru ca fragmentul
     * neinteles e chiar textul de care omul are nevoie ca sa repare. Gasit de S1 pe sursa,
     * inainte sa apuce sa masoare.
     *
     * ⚠️ Se pierde forma originala a claselor INTELESE („5A" devine „V A") — deliberat: dupa o
     * stergere, textul trebuie sa corespunda cu ce vede omul in oglinda. Sa taiem exact fragmentul
     * original ar cere sa stim care l-a produs, iar la duplicate contopite („5A, 5 a, V A" -> o
     * eticheta) intrebarea n-are un singur raspuns.
     */
    const sterge = (canonic: string) => onChange(textDupaStergere({ clase, neintelese }, canonic));

    const numarClase = clase.length;

    /**
     * „3 clase", dar „24 **de** clase".
     *
     * Romana cere „de" cand ultimele doua cifre sunt 00 sau intre 20 si 99 — deci si „120 de
     * clase", dar „101 clase". Nu e cosmetic la scara asta: o scoala cu opt ani si trei paralele
     * trece pragul. (Semnalat de S1.)
     */
    const eticheta = numarClase === 1
        ? '1 clasă'
        : `${numarClase} ${numarClase % 100 === 0 || numarClase % 100 >= 20 ? 'de clase' : 'clase'}`;

    return (
        <div className="space-y-3">
            {/* ⚠️ Eticheta NU repeta intrebarea din titlul ecranului: aceeasi intrebare de doua
                ori, si in doua registre («ați» in titlu, «ai» aici), suna a doua ecrane lipite.
                Titlul intreaba; eticheta spune ce se scrie. (Semnalat de S1.) */}
            <label htmlFor="camp-clase" className="block font-medium text-foreground">
                {label}
            </label>

            <Input
                id="camp-clase"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder="de exemplu: a V-a A, a V-a B, a VI-a A"
                autoComplete="off"
                aria-describedby={errorId ? `camp-clase-ajutor ${errorId}` : 'camp-clase-ajutor'}
                aria-invalid={errorId ? true : undefined}
            />

            <p id="camp-clase-ajutor" className="text-sm text-muted-foreground">
                Scrie-le cum îți vine — le înțelegem și dacă folosești cifre („5A") sau prescurtări.
            </p>

            {/* Oglinda: ce am inteles, in forma canonica, cu numarul LANGA etichete. */}
            {numarClase > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {clase.map((clasa) => (
                        <span
                            key={clasa.canonic}
                            data-eticheta-clasa={clasa.canonic}
                            className="inline-flex items-center gap-1 rounded-full border border-primary bg-light py-1 pl-3 pr-1 text-sm"
                        >
                            {clasa.eticheta}
                            {/* 🔴 `×` e tinta separata de eticheta, deci are nevoie de propriile
                                44px si de spatiu fata de vecini — la 360px etichetele inghesuite
                                sunt exact locul unde reapare defectul de la #84. */}
                            <button
                                type="button"
                                onClick={() => sterge(clasa.canonic)}
                                disabled={disabled}
                                aria-label={`Șterge clasa ${clasa.eticheta}`}
                                className="touch-target flex items-center justify-center rounded-full hover:bg-primary/10"
                            >
                                <X className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </span>
                    ))}
                    <span data-contor-clase={numarClase} className="text-sm text-muted-foreground">
                        {eticheta}
                    </span>
                </div>
            )}

            {/* Eroarea serverului: numeste FRAGMENTUL, nu campul, si NU goleste ce a scris omul.
                Campul are mai multe valori intr-unul singur, deci „verifica campul" n-ar ajuta. */}
            {fragmenteRespinse && fragmenteRespinse.length > 0 && (
                /* `data-eroare-clase` pe langa `role="alert"`: verdictul se sprijina pe marcaj
                   propriu, nu pe un rol generic care poate exista in zece locuri pe ecran.
                   Cerut de S1, si e ieftin acum — scump peste trei valuri. */
                <p role="alert" data-eroare-clase className="text-sm text-error">
                    {fragmenteRespinse.length === 1
                        ? `Nu am înțeles „${fragmenteRespinse[0]}”.`
                        : `Nu am înțeles: ${fragmenteRespinse.map((f) => `„${f}”`).join(', ')}.`}{' '}
                    Scrie-le ca „a V-a A”, despărțite prin virgulă.
                </p>
            )}
        </div>
    );
}
